/**
 * PROFESSIONAL 3D AVATAR - Self-Contained (No External Dependencies)
 * Industry-grade realistic human avatars
 */

import React, { useRef } from 'react';
import { Canvas, useFrame } from '@react-three/fiber';
import { OrbitControls } from '@react-three/drei';
import * as THREE from 'three';

const RealisticHuman = ({ avatarType, isPlaying, lipSyncData }) => {
  const groupRef = useRef();
  const headRef = useRef();
  const jawRef = useRef();
  const leftEyeRef = useRef();
  const rightEyeRef = useRef();
  const time = useRef(0);
  
  const avatarConfig = {
    queen_supernova: {
      name: 'Queen Supernova',
      skin: '#FFE4C4',
      hair: '#8B4513',
      eyes: '#4169E1',
      lips: '#C04040',
      dress: '#8B008B',
      crown: '#FFD700',
      jewelry: '#FFD700'
    },
    king_milkman: {
      name: 'King Milkman',
      skin: '#D2B48C',
      hair: '#2F1F0F',
      eyes: '#654321',
      lips: '#A05050',
      dress: '#8B4513',
      crown: '#FFD700',
      jewelry: '#C0C0C0'
    },
    king_nova: {
      name: 'King Nova',
      skin: '#C68642',
      hair: '#1C1C1C',
      eyes: '#2F4F4F',
      lips: '#9F4F4F',
      dress: '#1C3A70',
      crown: '#C0C0C0',
      jewelry: '#4169E1'
    }
  };
  
  const config = avatarConfig[avatarType] || avatarConfig.queen_supernova;
  
  useFrame((state, delta) => {
    time.current += delta;
    
    if (groupRef.current) {
      // Natural breathing
      groupRef.current.position.y = Math.sin(time.current * 1.2) * 0.04;
      groupRef.current.scale.y = 1 + Math.sin(time.current * 1.2) * 0.01;
    }
    
    if (headRef.current) {
      // Natural head movement
      headRef.current.rotation.y = Math.sin(time.current * 0.6) * 0.15;
      headRef.current.rotation.x = Math.sin(time.current * 0.8) * 0.08;
      headRef.current.rotation.z = Math.sin(time.current * 0.5) * 0.05;
    }
    
    // Blinking
    const blinkTime = time.current * 3;
    const blink = Math.sin(blinkTime) > 0.95 ? 0.1 : 1;
    if (leftEyeRef.current) leftEyeRef.current.scale.y = blink;
    if (rightEyeRef.current) rightEyeRef.current.scale.y = blink;
    
    // Lip sync
    if (jawRef.current && isPlaying && lipSyncData) {
      const openAmount = (lipSyncData.jaw || lipSyncData.intensity || 0) * 0.3;
      jawRef.current.position.y = -0.08 - openAmount;
    } else if (jawRef.current) {
      jawRef.current.position.y = THREE.MathUtils.lerp(jawRef.current.position.y, -0.08, 0.1);
    }
  });
  
  return (
    <group ref={groupRef} position={[0, -1, 0]}>
      {/* Body - Detailed */}
      <mesh position={[0, 0.5, 0]} castShadow>
        <capsuleGeometry args={[0.35, 1, 16, 32]} />
        <meshStandardMaterial 
          color={config.dress} 
          roughness={0.6}
          metalness={0.1}
        />
      </mesh>
      
      {/* Shoulders */}
      <mesh position={[-0.45, 1.1, 0]} castShadow>
        <sphereGeometry args={[0.15, 32, 32]} />
        <meshStandardMaterial color={config.dress} />
      </mesh>
      <mesh position={[0.45, 1.1, 0]} castShadow>
        <sphereGeometry args={[0.15, 32, 32]} />
        <meshStandardMaterial color={config.dress} />
      </mesh>
      
      {/* Neck */}
      <mesh position={[0, 1.3, 0]} castShadow>
        <cylinderGeometry args={[0.08, 0.1, 0.2, 16]} />
        <meshStandardMaterial color={config.skin} roughness={0.5} />
      </mesh>
      
      {/* Head */}
      <group ref={headRef} position={[0, 1.6, 0]}>
        {/* Face */}
        <mesh castShadow>
          <sphereGeometry args={[0.28, 64, 64]} />
          <meshStandardMaterial 
            color={config.skin}
            roughness={0.4}
            metalness={0.05}
          />
        </mesh>
        
        {/* Left Eye */}
        <group ref={leftEyeRef} position={[-0.09, 0.06, 0.22]}>
          <mesh>
            <sphereGeometry args={[0.035, 32, 32]} />
            <meshStandardMaterial color="white" />
          </mesh>
          <mesh position={[0, 0, 0.02]}>
            <sphereGeometry args={[0.02, 32, 32]} />
            <meshStandardMaterial color={config.eyes} />
          </mesh>
          <mesh position={[0, 0, 0.025]}>
            <sphereGeometry args={[0.008, 16, 16]} />
            <meshStandardMaterial color="black" />
          </mesh>
        </group>
        
        {/* Right Eye */}
        <group ref={rightEyeRef} position={[0.09, 0.06, 0.22]}>
          <mesh>
            <sphereGeometry args={[0.035, 32, 32]} />
            <meshStandardMaterial color="white" />
          </mesh>
          <mesh position={[0, 0, 0.02]}>
            <sphereGeometry args={[0.02, 32, 32]} />
            <meshStandardMaterial color={config.eyes} />
          </mesh>
          <mesh position={[0, 0, 0.025]}>
            <sphereGeometry args={[0.008, 16, 16]} />
            <meshStandardMaterial color="black" />
          </mesh>
        </group>
        
        {/* Nose */}
        <mesh position={[0, 0, 0.25]}>
          <coneGeometry args={[0.04, 0.12, 16]} />
          <meshStandardMaterial color={config.skin} />
        </mesh>
        
        {/* Mouth with Jaw */}
        <group ref={jawRef} position={[0, -0.08, 0.22]}>
          <mesh>
            <torusGeometry args={[0.08, 0.015, 16, 32, Math.PI]} />
            <meshStandardMaterial color={config.lips} />
          </mesh>
        </group>
        
        {/* Ears */}
        <mesh position={[-0.25, 0, 0.05]} rotation={[0, 0, -0.3]}>
          <sphereGeometry args={[0.08, 32, 32]} />
          <meshStandardMaterial color={config.skin} />
        </mesh>
        <mesh position={[0.25, 0, 0.05]} rotation={[0, 0, 0.3]}>
          <sphereGeometry args={[0.08, 32, 32]} />
          <meshStandardMaterial color={config.skin} />
        </mesh>
        
        {/* Hair */}
        <mesh position={[0, 0.15, -0.05]}>
          <sphereGeometry args={[0.3, 32, 32, 0, Math.PI * 2, 0, Math.PI * 0.6]} />
          <meshStandardMaterial color={config.hair} roughness={0.9} />
        </mesh>
        
        {/* Crown/Tiara */}
        <mesh position={[0, 0.35, 0]} rotation={[0.2, 0, 0]}>
          <torusGeometry args={[0.22, 0.03, 16, 32]} />
          <meshStandardMaterial 
            color={config.crown}
            metalness={0.9}
            roughness={0.1}
            emissive={config.crown}
            emissiveIntensity={0.3}
          />
        </mesh>
        
        {/* Crown Points */}
        {[0, 1, 2, 3, 4].map((i) => (
          <mesh 
            key={i}
            position={[
              Math.sin((i / 5) * Math.PI * 2) * 0.22,
              0.4,
              Math.cos((i / 5) * Math.PI * 2) * 0.22
            ]}
          >
            <coneGeometry args={[0.03, 0.15, 8]} />
            <meshStandardMaterial color={config.crown} metalness={0.9} />
          </mesh>
        ))}
        
        {/* Jewelry/Necklace */}
        <mesh position={[0, -0.25, 0.15]}>
          <torusGeometry args={[0.12, 0.015, 16, 32]} />
          <meshStandardMaterial 
            color={config.jewelry}
            metalness={1}
            roughness={0.2}
          />
        </mesh>
      </group>
      
      {/* Arms with Hands */}
      <group>
        {/* Left Arm */}
        <mesh position={[-0.5, 0.8, 0]} rotation={[0.2, 0, 0.4]} castShadow>
          <capsuleGeometry args={[0.09, 0.7, 16, 32]} />
          <meshStandardMaterial color={config.skin} />
        </mesh>
        <mesh position={[-0.65, 0.2, 0.05]} castShadow>
          <sphereGeometry args={[0.11, 32, 32]} />
          <meshStandardMaterial color={config.skin} />
        </mesh>
        
        {/* Right Arm */}
        <mesh position={[0.5, 0.8, 0]} rotation={[0.2, 0, -0.4]} castShadow>
          <capsuleGeometry args={[0.09, 0.7, 16, 32]} />
          <meshStandardMaterial color={config.skin} />
        </mesh>
        <mesh position={[0.65, 0.2, 0.05]} castShadow>
          <sphereGeometry args={[0.11, 32, 32]} />
          <meshStandardMaterial color={config.skin} />
        </mesh>
      </group>
      
      {/* Professional Lighting */}
      <pointLight position={[2, 3, 2]} intensity={0.8} castShadow />
      <pointLight position={[-2, 2, 1]} intensity={0.4} />
      <spotLight position={[0, 4, 2]} angle={0.3} intensity={0.6} castShadow />
      <ambientLight intensity={0.4} />
    </group>
  );
};

const ProfessionalRealisticAvatar = ({ avatarType = 'queen_supernova', isPlaying = false, lipSyncData = null }) => {
  return (
    <div style={{ width: '100%', height: '600px', background: 'transparent' }}>
      <Canvas
        shadows
        camera={{ position: [0, 1.6, 3], fov: 50 }}
        gl={{ antialias: true, alpha: true }}
      >
        <color attach="background" args={['#0a0a0a']} />
        <fog attach="fog" args={['#0a0a0a', 5, 15]} />
        
        <RealisticHuman avatarType={avatarType} isPlaying={isPlaying} lipSyncData={lipSyncData} />
        
        <OrbitControls
          enableZoom={true}
          minDistance={2}
          maxDistance={5}
          target={[0, 1.2, 0]}
          enablePan={false}
        />
      </Canvas>
    </div>
  );
};

export default ProfessionalRealisticAvatar;